import React, { useEffect, useMemo, useState } from 'react';

import {
  Flex, Text, HStack, Stack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import GoogleMapReact from 'google-map-react';

import { Icon } from 'components';
import { ActivityStatus } from 'enums/status';
import mapStyle from 'theme/map';
import { getPercentage } from 'utils/parsers';

export default function Map({ routes, expandedItems, toggleExpanded }) {
  const [coords, setCoords] = useState({ lat: -23.56178964762915, lng: -46.65593356608675 });

  useEffect(() => {
    navigator.geolocation.getCurrentPosition((location) => {
      setCoords({ lat: location.coords.latitude, lng: location.coords.longitude });
    });
  }, []);

  const markers = useMemo(() => routes.reduce((prev, route) => {
    const {
      lastUpdateLatitude,
      lastUpdateLongitude,
    } = [...route.activities.filter(
      (activity) => activity.lastUpdateLatitude && activity.lastUpdateLongitude,
    )]?.reverse()?.[0] ?? {};

    if (lastUpdateLatitude && lastUpdateLongitude) {
      return [...prev, {
        route,
        lat: lastUpdateLatitude,
        lng: lastUpdateLongitude,
      }];
    }
    return prev;
  }, []), [routes]);

  function Marker({ route, ...rest }) {
    const completed = useMemo(
      () => getPercentage(route.completedActivities, route.activitiesCount)
        .toFixed(0),
      [route]);

    const isAlert = useMemo(
      () => route.activities.find((activity) => activity.status === ActivityStatus.Alert),
      [route]);

    const isExpanded = expandedItems.has(route.id);
    return (
      <Flex
        flexDir="column"
        alignItems="center"
        position="relative">
        <Stack
          zIndex={9999}
          borderRadius={6}
          bg="white"
          p={4}
          bottom={30}
          position="absolute"
          hidden={!isExpanded}>
          <HStack
            alignItems="center"
            justifyContent="center">
            <Text
              cursor="pointer"
              as="a"
              href={`/routes/${route.routeId}`}
              variant="smallBold"
              color="gray.600">
              {route.code}
            </Text>
            <Text
              variant="xSmallSemiBold"
              color="gray.500">
              {route.name}
            </Text>
          </HStack>
          <Flex
            alignItems="center"
            justifyContent="space-between">
            <Flex
              mr={4}
              borderRadius={8}
              height={2}
              width="100%"
              bg="gray.50">
              <Flex
                as={motion.div}
                borderRadius={8}
                height={2}
                initial={{ width: 0 }}
                exit={{ width: 0 }}
                animate={{ width: `${completed}%` }}
                bg={isAlert ? 'system.error.default' : 'primary.default'}
              />
            </Flex>
            <Text
              variant="smallBold"
              color={isAlert ? 'system.error.default' : 'primary.default'}>
              {completed}%
            </Text>
          </Flex>
        </Stack>
        <Flex
          onClick={() => toggleExpanded(route.id)}
          w="min"
          h="min"
          {...rest}
          alignItems="flex-start">
          <Icon
            size="2xl"
            color={isExpanded ? 'primary.default' : 'secondary.500'}
            name="map-marker-alt"
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      shadow="inset 2px 2px 2px rgba(0, 0, 0, 0.15);"
      borderRadius={8}
      overflow="hidden"
      width="100%"
      alignItems="center"
      justifyContent="center"
      height={200}>
      <Flex
        borderRadius={8}
        overflow="hidden"
        width="calc(100% - 4px)"
        height={196}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_KEY }}
          defaultZoom={11}
          defaultCenter={coords}
          yesIWantToUseGoogleMapApiInternals
          options={{
            fullscreenControl: false,
            zoomControl: false,
            styles: mapStyle,
          }}>
          {markers?.map((marker) => (
            <Marker
              lat={marker.lat}
              lng={marker.lng}
              route={marker.route}
            />
          ))}
        </GoogleMapReact>
      </Flex>
    </Flex>
  );
}
